import type { ColumnDef } from '@tanstack/react-table';
import {
  Card,
  InputDateTime,
  InputSelect,
  InputText,
  TableListData,
} from 'components';
import { Button, InputWraper, useForm, IUseFormStructureItem } from 'modules';
import React, { useCallback, useState } from 'react';
import { useExportRequestWihtdraw, useGetRequestWihtdraw } from 'services';
import {
  GetAllListWithdrawRequestEntity,
  GetOwnListWithdrawRequestEntity,
} from 'services/openapi';
import { exportHeaders, type FilterType, getInitFilterValues } from './config';
import { useLocation } from 'react-router-dom';
import * as Yup from 'yup';
import { convertSelectStatusOptions, convertVariables } from './config';
import { WithdrawRequestEntity } from 'services/openapi';
import ApproveRequestWithdraw from './approve';
import RejectRequestWithdraw from './reject';
import { RequestWithdrawStatus } from 'configs/constants';
import { format } from 'date-fns';
import { NumberUtils } from 'modules';
import { RequestWithdrawStatusColor } from 'configs/constants';
import './CheckRequestWithdraw.scss';
import { DEFAULT_PAGE } from 'configs';
import ExcelJS from 'exceljs';
import saveAs from 'file-saver';
import {
  formatColumn,
  formatRowData,
  formatRowDataTotal,
} from 'configs/cell_xlsx';
import { checkRolesIncludeRoles, checkValidActionRoleAdmin, removeVietnameseTones } from 'utils/helpers';
import { Actions } from 'configs/actions';

const CheckRequestWithdrawList = () => {
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const initFilterValues = getInitFilterValues(urlParams);

  const [variables, setVariables] = useState<FilterType>(initFilterValues);
  const [dataTable, setDataTable] = useState([]);
  const [openPopUpAcceptRequest, setOpenPopUpAcceptRequest] = useState(false);
  const [openPopUpRejectRequest, setOpenPopUpRejectRequest] = useState(false);
  const [itemId, setItemId] = useState<WithdrawRequestEntity | null>(null);

  const handleFilter = useCallback(
    (e: FilterType) => {
      setVariables({
        ...variables,
        ...e,
        refreshData: variables.refreshData + 1,
      });
    },
    [variables],
  );

  const filterOptions: IUseFormStructureItem[] = [
    {
      name: 'phoneNumber',
      label: 'Tài khoản',
      defaultValue: initFilterValues.phoneNumber,
    },
    {
      name: 'transactionStatus',
      label: 'Trạng thái',
      defaultValue: initFilterValues.transactionStatus,
      validate: Yup.string().required('Vui lòng chọn Trạng thái'),
    },
    {
      name: 'fromTime',
      label: 'Ngày & giờ bắt đầu',
      defaultValue: initFilterValues.fromTime,
      validate: Yup.string()
        .required('Vui lòng chọn Ngày & giờ bắt đầu')
        .test(
          'comp-fromdate-todate',
          'Ngày & giờ bắt đầu phải nhỏ hơn Ngày & giờ kết thúc',
          function (value) {
            if (value)
              return (
                new Date(value).getTime() <
                new Date(this.parent?.toTime).getTime()
              );
            return false;
          },
        )
        .test(
          'comp-fromdate-todate-60-days',
          'Khoảng cách Ngày & giờ bắt đầu và Ngày & giờ kết thúc không vượt quá 60 ngày',
          function (value) {
            if (value) {
              const dateEnd: Date = new Date(this.parent?.toTime);
              dateEnd.setHours(0, 0, 0, 0);
              dateEnd.setDate(dateEnd.getDate() - 60);
              const dateStart: Date = new Date(value);
              dateEnd.setHours(0, 0, 0, 0);
              return dateEnd.getTime() <= dateStart.getTime();
            }
            return false;
          },
        ),
    },
    {
      name: 'toTime',
      label: 'Ngày & giờ kết thúc',
      defaultValue: initFilterValues.toTime,
      validate: Yup.string()
        .required('Vui lòng chọn Ngày & giờ kết thúc')
        .test(
          'comp-fromdate-todate',
          'Ngày & giờ kết thúc phải lớn hơn Ngày & giờ bắt đầu',
          function (value) {
            if (value)
              return (
                new Date(this.parent?.fromTime).getTime() <
                new Date(value).getTime()
              );
            return false;
          },
        )
        .test(
          'comp-fromdate-todate-60-days',
          'Khoảng cách Ngày & giờ bắt đầu và Ngày & giờ kết thúc không vượt quá 60 ngày',
          function (value) {
            if (value) {
              const dateEnd: Date = new Date(value);
              dateEnd.setHours(0, 0, 0, 0);
              dateEnd.setDate(dateEnd.getDate() - 60);
              const dateStart: Date = new Date(this.parent?.fromTime);
              dateEnd.setHours(0, 0, 0, 0);
              return dateEnd.getTime() <= dateStart.getTime();
            }
            return false;
          },
        ),
    },
  ];

  const columnsDefined: ColumnDef<WithdrawRequestEntity>[] = [
    {
      accessorKey: 'phoneNumber',
      header: 'Tài khoản',
    },
    {
      accessorKey: 'name',
      header: 'Ngân hàng',
      cell: (info) => {
        // return <div className='text-center'>{info.row.original?.bankAccount?.bank?.shortName} - {info.row.original?.bankAccount?.bank?.name}</div>
        return (
          <div className='text-center'>
            {info.row.original?.bankAccount?.bank?.shortName}
          </div>
        );
      },
    },
    {
      accessorKey: 'accountName',
      header: 'Tên tài khoản',
      cell: (info) => {
        return (
          <div className='text-center'>
            {info.row.original?.bankAccount?.accountName}
          </div>
        );
      },
    },
    {
      accessorKey: 'accountNumber',
      header: 'Số tài khoản',
      cell: (info) => {
        return (
          <div className='text-center'>
            {info.row.original?.bankAccount?.accountNumber}
          </div>
        );
      },
    },
    {
      accessorKey: 'amount',
      header: 'Số tiền',
      cell: (info) => {
        return (
          <div className='textDanger'>
            {info.getValue()
              ? NumberUtils.toFormatNumber(Number(info.getValue()))
              : '0'}
          </div>
        );
      },
    },
    {
      accessorKey: 'status',
      header: 'Trạng thái',
      cell: (info) => (
        <div
          className={
            RequestWithdrawStatusColor[
              info.getValue() as keyof typeof RequestWithdrawStatusColor
            ]
          }
        >
          {String(info.getValue())}
        </div>
      ),
    },
    // {
    //   accessorKey: 'transactions',
    //   header: 'GD rút điểm',
    //   cell: (info) => {
    //     const txs: any  = info.getValue();
    //     console.log(txs)
    //     if(!txs || txs?.length == 0) return <></>
    //     const txReuest = txs.find((tx: any) => tx.transactionType === PointTransactionTypesColor['Rút điểm']);
    //     return <div className="textInfo">{txReuest.id}</div>
    //   }
    // },
    // {
    //   accessorKey: 'transactions',
    //   header: 'GD hoàn điểm',
    //   cell: (info) => {
    //     const txs: any  = info.getValue();
    //     if(!txs || txs?.length == 0) return <></>
    //     const txReuest = txs.find((tx: any) => tx.transactionType === PointTransactionTypesColor['Hoàn điểm']);
    //     return <div className="textDanger">{txReuest.id}</div>
    //   }
    // },
    {
      accessorKey: 'createdAt',
      header: 'Thời gian gửi',
      cell: (info) =>
        format(new Date(String(info.getValue())), 'HH:mm:ss dd/MM/yyyy'),
    },
    {
      accessorKey: 'confirmAt',
      header: 'Thời gian duyệt',
      cell: (info) =>
        info.getValue()
          ? format(new Date(String(info.getValue())), 'HH:mm:ss dd/MM/yyyy')
          : '',
    },
    {
      accessorKey: 'rejectReason',
      header: 'Nguyên nhân từ chối',
    },
    {
      accessorKey: 'transactionImageUrl',
      header: 'Hình ảnh',
      cell: (info) => {
        // if (!info.row.original.transactionImageUrl) return <div className="action__upload">Upload</div>
        if (!info.row.original.transactionImageUrl) return <></>;
        return (
          <div
            style={{ cursor: 'pointer' }}
            onClick={() => {
              window.open(info.row.original.transactionImageUrl, '_blank');
            }}
          >
            <img
              style={{ width: 'auto', height: 25, objectFit: 'contain' }}
              src={info.row.original.transactionImageUrl}
              alt='Hình ảnh'
            />
          </div>
        );
      },
    },
    {
      id: 'action',
      header: 'Thao tác',
      cell: (info) => {
        return info.row.original.status ===
          RequestWithdrawStatus['Đang xử lý'] ? (
          <div className='action'>
            {checkValidActionRoleAdmin(Actions.WithdrawPoint_ApproveRequest) && <div
              className='action__accept'
              onClick={() => {
                setOpenPopUpAcceptRequest(true);
                setItemId(info.row.original);
              }}
            >
              Duyệt
            </div>}

            {checkValidActionRoleAdmin(Actions.WithdrawPoint_RejectRequest) && <div
              className='action__reject'
              onClick={() => {
                setOpenPopUpRejectRequest(true);
                setItemId(info.row.original);
              }}
            >
              Từ chối
            </div>}
          </div>
        ) : (
          <></>
        );
      },
    },
  ];

  const { getInputProps, handleSubmit } = useForm({
    structure: filterOptions,
    onSubmit: handleFilter,
  });

  const { isFetching: isLoadingExport, refetch } = useExportRequestWihtdraw({
    ...convertVariables(variables),
    page: '1',
    pageSize: String(
      (dataTable as unknown as GetAllListWithdrawRequestEntity)?.total ?? 0,
    ),
  });

  const handleExportExcel = async () => {
    // Add some data rows
    const allData = await refetch({
      ...convertVariables(variables),
      page: DEFAULT_PAGE,
      pageSize: String(
        (dataTable as unknown as GetAllListWithdrawRequestEntity)?.total,
      ),
    } as any);

    const dataArray = allData?.data?.data;

    const result = Array.isArray(dataArray) ? dataArray : [];

    //
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Sheet1');

    // Columns
    const headerColumn = exportHeaders.map((header) => {
      return {
        header: header,
        key: header,
        width: 40,
      };
    });
    worksheet.columns = headerColumn;

    // Total
    const totalAmount = result
      .map((item: WithdrawRequestEntity) => item.amount)
      .reduce((acc, curr) => acc + curr, 0);

    const getValueSumAmount = NumberUtils.toFormatNumber(
      allData.data?.total as number,
    );
    const row = worksheet.addRow([
      '',
      '',
      'Tổng số tiền (Total)',
      totalAmount,
      '',
      'Tổng lệnh (Total order)',
      getValueSumAmount,
      '',
      '',
    ]);
    row.eachCell((cell, colNumber) => {
      if (
        colNumber === 3 ||
        colNumber === 4 ||
        colNumber === 6 ||
        colNumber === 7
      ) {
        formatRowDataTotal(cell);
      }
    });

    // Headers
    const firstRow = worksheet.getRow(1);
    firstRow.eachCell((cell) => formatColumn(cell));

    // Data
    result.map((item: WithdrawRequestEntity, index: number) => {
      const row = worksheet.addRow([
        index + 1,
        removeVietnameseTones(item.bankAccount.accountName),
        'Rut diem tu tai khoan ' + item.phoneNumber,
        item.amount,
        item.bankAccount.accountNumber,
        removeVietnameseTones(`${item.bankAccount.bank.code}`) +
          '-' +
          removeVietnameseTones(`${item.bankAccount.bank.name}`),
        item.bankAccount.bank.headOfficeCitadCode || 'KHÔNG HỖ TRỢ',
      ]);
      row.eachCell((cell) => formatRowData(cell));
    });

    // Generate Excel and trigger file download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const now = new Date();
    saveAs(
      blob,
      `Danh_sach_yeu_cau_rut_diem_${now.toLocaleTimeString('vn-VN', { hour12: false }).replace(/:/g, '_')}_${now.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '_')}.xlsx`,
    );
  };

  return (
    <>
      <Card className='container-fluid mb20'>
        <form onSubmit={handleSubmit}>
          <div className='row'>
            <div className='col-lg-3 col-12'>
              <InputWraper
                inputProps={getInputProps('phoneNumber')}
                renderInput={InputText}
              />
            </div>

            <div className='col-lg-3 col-12'>
              <InputWraper
                inputProps={getInputProps('transactionStatus')}
                isRequired
                renderInput={(props) => (
                  <InputSelect
                    {...props}
                    options={convertSelectStatusOptions()}
                  />
                )}
              />
            </div>

            <div className='col-lg-3 col-12'>
              <InputWraper
                inputProps={getInputProps('fromTime')}
                isRequired
                renderInput={InputDateTime}
              />
            </div>

            <div className='col-lg-3 col-12'>
              <InputWraper
                inputProps={getInputProps('toTime')}
                isRequired
                renderInput={InputDateTime}
              />
            </div>
          </div>
          {checkValidActionRoleAdmin(Actions.WithdrawPoint_ListRequest) && <div className='textCenter mt16 wrapButtonFilter'>
            <Button label={'Tìm kiếm'} type='submit' buttonType='success' />
            <Button
              className='ml15'
              onClick={handleExportExcel}
              label={'Tải về DS'}
              type='submit'
              buttonType='info'
              isLoading={isLoadingExport}
            />
          </div>}
        </form>
      </Card>

      <TableListData<GetOwnListWithdrawRequestEntity>
        columns={columnsDefined as ColumnDef<GetOwnListWithdrawRequestEntity>[]}
        useQuery={useGetRequestWihtdraw}
        variables={variables}
        setVariables={setVariables}
        convertVariables={convertVariables}
        orderColumn
        callBackData={setDataTable}
      />

      {openPopUpAcceptRequest && (
        <ApproveRequestWithdraw
          data={itemId}
          onClose={() => setOpenPopUpAcceptRequest(false)}
          forceUpdateTable={handleSubmit}
        ></ApproveRequestWithdraw>
      )}
      {openPopUpRejectRequest && (
        <RejectRequestWithdraw
          data={itemId}
          onClose={() => setOpenPopUpRejectRequest(false)}
          forceUpdateTable={handleSubmit}
        ></RejectRequestWithdraw>
      )}
    </>
  );
};

export default CheckRequestWithdrawList;
